import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { isValidEmail, validatePasswordStrength } from '../utils/security';

// ── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = ({ className = 'w-3.5 h-3.5' }) => (
    <svg className={`${className} animate-spin`} fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
);

// ── Toast notification ────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => {
    useEffect(() => {
        const t = setTimeout(onClose, 4000);
        return () => clearTimeout(t);
    }, [onClose]);

    const isSuccess = type === 'success';
    return (
        <div
            className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all
                ${isSuccess ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}
        >
            {isSuccess ? (
                <svg className="w-4 h-4 text-green-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            ) : (
                <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            )}
            <span>{message}</span>
            <button onClick={onClose} className="ml-1 opacity-60 hover:opacity-100">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

// ── Image compression helper ──────────────────────────────────────────────────
async function compressImage(file) {
    const compressed = await imageCompression(file, {
        maxSizeMB: 0.1,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
        maxIteration: 15,
        initialQuality: 0.85,
    });
    return new File([compressed], file.name, { type: file.type });
}

// ── ProfilePage ───────────────────────────────────────────────────────────────
const ProfilePage = () => {
    const navigate = useNavigate();
    const { user, logout, refreshUser } = useAuth();

    const userId = user?.id || user?.userId || user?._id || '';

    // ── Notifications ──────────────────────────────────────────────────────────
    const [toast, setToast] = useState(null);
    const showSuccess = (message) => setToast({ message, type: 'success' });
    const showError = (message) => setToast({ message, type: 'error' });

    // ── User data ──────────────────────────────────────────────────────────────
    const [userData, setUserData] = useState({ name: '', email: '', phone: '', role: '', roleLabel: '' });
    const [userDataLoading, setUserDataLoading] = useState(false);

    // ── Email form ─────────────────────────────────────────────────────────────
    const [newEmail, setNewEmail] = useState('');
    const [emailLoading, setEmailLoading] = useState(false);

    // ── Phone form ─────────────────────────────────────────────────────────────
    const [newPhone, setNewPhone] = useState('');
    const [phoneLoading, setPhoneLoading] = useState(false);

    // ── Password form ──────────────────────────────────────────────────────────
    const [pwForm, setPwForm] = useState({ newPassword: '', confirmPassword: '' });
    const [pwLoading, setPwLoading] = useState(false);
    const [showPw, setShowPw] = useState({ newPassword: false, confirmPassword: false });

    // ── Avatar ─────────────────────────────────────────────────────────────────
    const [avatarPreview, setAvatarPreview] = useState('');
    const [avatarLoading, setAvatarLoading] = useState(false);
    const fileInputRef = useRef(null);

    // ── User menu ──────────────────────────────────────────────────────────────
    const [showUserMenu, setShowUserMenu] = useState(false);
    const userMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Fetch user on mount ────────────────────────────────────────────────────
    useEffect(() => {
        if (!userId) return;
        const controller = new AbortController();
        const fetchUser = async () => {
            setUserDataLoading(true);
            try {
                const res = await api.get(`/api/user/users/${userId}`, { signal: controller.signal });
                const u = res.data?.user || res.data || {};
                setUserData({
                    name: u.name || '',
                    email: u.email || '',
                    phone: u.phone || '',
                    role: u.role || '',
                    roleLabel: u.roleLabel || '',
                });
                setNewEmail(u.email || '');
                setNewPhone(u.phone || '');
                let imgUrl = u.profileImageUrl || '';
                if (imgUrl && imgUrl.startsWith('/') && !imgUrl.startsWith('//')) {
                    const base = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
                    imgUrl = `${base}${imgUrl}`;
                }
                setAvatarPreview(imgUrl);
            } catch (err) {
                if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
                    console.error('Failed to fetch user data:', err);
                }
            } finally {
                setUserDataLoading(false);
            }
        };
        fetchUser();
        return () => controller.abort();
    }, [userId]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleUpdateEmail = async (e) => {
        e.preventDefault();
        if (!newEmail) { showError('Email is required.'); return; }
        if (!isValidEmail(newEmail)) { showError('Please enter a valid email address.'); return; }
        setEmailLoading(true);
        try {
            await api.post('/api/user/updateEmail', { newEmail, userId });
            showSuccess('Email updated successfully.');
            setUserData(d => ({ ...d, email: newEmail }));
            if (refreshUser) refreshUser();
        } catch (err) {
            showError(err.response?.data?.message || 'Failed to update email.');
        } finally {
            setEmailLoading(false);
        }
    };

    const handleUpdatePhone = async (e) => {
        e.preventDefault();
        if (!newPhone.trim()) { showError('Phone number is required.'); return; }
        setPhoneLoading(true);
        try {
            await api.post('/api/user/updatePhoneNumber', { userId, phone: newPhone.trim() });
            showSuccess('Phone number updated successfully.');
            setUserData(d => ({ ...d, phone: newPhone.trim() }));
        } catch (err) {
            showError(err.response?.data?.message || 'Failed to update phone number.');
        } finally {
            setPhoneLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (pwForm.newPassword !== pwForm.confirmPassword) { showError('Passwords do not match.'); return; }
        const check = validatePasswordStrength(pwForm.newPassword);
        if (!check.isValid) { showError(check.message || 'Password does not meet requirements.'); return; }
        setPwLoading(true);
        try {
            await api.post('/api/user/changePassword', { newPassword: pwForm.newPassword, userId });
            showSuccess('Password changed successfully.');
            setPwForm({ newPassword: '', confirmPassword: '' });
        } catch (err) {
            showError(err.response?.data?.message || 'Failed to change password.');
        } finally {
            setPwLoading(false);
        }
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
            showError('Only JPEG or PNG files are allowed.');
            e.target.value = '';
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => setAvatarPreview(ev.target.result);
        reader.readAsDataURL(file);
        setAvatarLoading(true);
        try {
            const compressed = await compressImage(file);
            const formData = new FormData();
            formData.append('image', compressed);
            formData.append('userId', userId);
            const res = await api.put(`/api/user/myprofileUpload/${userId}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const imageUrl = res.data?.data?.imageUrl || res.data?.fileUrl || '';
            if (imageUrl) {
                setAvatarPreview(`${imageUrl}?t=${Date.now()}`);
                if (refreshUser) refreshUser();
            }
            showSuccess('Profile photo updated successfully.');
        } catch (err) {
            showError(err.response?.data?.message || err.message || 'Failed to upload image.');
        } finally {
            setAvatarLoading(false);
            e.target.value = '';
        }
    };

    const handleSignOut = async () => {
        setShowUserMenu(false);
        try {
            if (logout) await logout();
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            navigate('/login');
        }
    };

    const initials = (userData.name || user?.name || 'U').charAt(0).toUpperCase();

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Toast */}
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            {/* ── Navbar ─────────────────────────────────────────────────────── */}
            <nav
                className="bg-gradient-to-b from-slate-900 to-slate-800 border-b border-slate-700/60 flex-shrink-0"
                style={{ boxShadow: '0 4px 24px 0 rgba(99,102,241,0.10), 0 1px 0 0 rgba(255,255,255,0.04) inset' }}
            >
                <div className="w-full px-4">
                    <div className="flex items-center justify-between gap-4">
                        {/* Brand */}
                        <div className="py-2 flex items-center gap-2 flex-shrink-0">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="w-8 h-8 rounded-lg shadow-lg flex items-center justify-center bg-gray-700 text-white font-bold text-base select-none hover:bg-gray-600 transition-colors"
                                title="Back to Dashboard"
                            >
                                B
                            </button>
                        </div>

                        {/* Nav tab */}
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="px-3 py-2 text-xs font-semibold transition-all duration-300 rounded-t-lg text-slate-400 hover:bg-slate-700/50 hover:text-white flex items-center gap-1.5"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                </svg>
                                Dashboard
                            </button>
                        </div>

                        {/* Right side: user menu */}
                        <div className="py-2 flex items-center gap-3 flex-shrink-0 ml-auto">
                            <div className="relative flex-shrink-0" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu((v) => !v)}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-700/50 rounded-lg text-white text-xs hover:bg-slate-700/70 transition-all duration-200"
                                    aria-label="User menu"
                                >
                                    <div className="w-7 h-7 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 shadow-md overflow-hidden">
                                        {avatarPreview ? (
                                            <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                        ) : (
                                            <span className="text-white text-xs font-bold">{initials}</span>
                                        )}
                                    </div>
                                    <span className="whitespace-nowrap max-w-[180px] truncate">{userData.email || user?.email || '—'}</span>
                                    <svg className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {showUserMenu && (
                                    <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                                        <div className="px-4 py-3 border-b border-gray-100">
                                            <p className="text-xs text-gray-500 font-medium">User</p>
                                            <p className="text-xs text-gray-900 truncate mt-1">{userData.email || user?.email || '—'}</p>
                                        </div>
                                        {/* My Profile — active */}
                                        <button
                                            onClick={() => setShowUserMenu(false)}
                                            className="w-full text-left px-4 py-2.5 text-xs font-medium text-indigo-700 bg-indigo-50 flex items-center gap-2"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            My Profile
                                        </button>
                                        <button
                                            onClick={handleSignOut}
                                            className="w-full text-left px-4 py-2.5 text-xs font-medium text-gray-700 hover:text-indigo-700 hover:bg-indigo-50 flex items-center gap-2 border-t border-gray-100 transition-colors"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                            </svg>
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* ── Page body ──────────────────────────────────────────────────── */}
            <div className="container mx-auto px-4 py-8 max-w-2xl">

                {/* Profile Picture */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-sm font-bold text-gray-900 mb-4">Profile Picture</h2>
                    <div className="flex items-center gap-5">
                        <div className="relative">
                            {avatarPreview
                                ? <img src={avatarPreview} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200 shadow" />
                                : <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-2xl font-bold border-2 border-gray-200 shadow">{initials}</div>
                            }
                            {avatarLoading && (
                                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                                    <Spinner className="w-5 h-5 text-white" />
                                </div>
                            )}
                        </div>
                        <div>
                            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={handleAvatarChange} />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={avatarLoading}
                                className="px-4 py-2 text-xs font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white rounded-lg shadow-md shadow-indigo-500/30 disabled:opacity-50 transition-colors"
                            >
                                {avatarLoading ? 'Uploading...' : 'Change Photo'}
                            </button>
                            <p className="text-[11px] text-gray-400 mt-1.5">JPEG or PNG only</p>
                        </div>
                    </div>
                </div>

                {/* Personal Information */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-sm font-bold text-gray-900 mb-1">Personal Information</h2>
                    <p className="text-xs text-gray-500 mb-4">Your account details.</p>
                    {userDataLoading ? (
                        <div className="flex items-center gap-2 py-4">
                            <Spinner className="w-4 h-4 text-gray-400" />
                            <span className="text-xs text-gray-400">Loading...</span>
                        </div>
                    ) : (
                        <dl className="space-y-3">
                            {[
                                { label: 'Full Name', value: userData.name },
                                { label: 'Phone Number', value: userData.phone },
                                { label: 'Role', value: userData.roleLabel || userData.role },
                            ].map(({ label, value }) => (
                                <div key={label} className="flex items-start gap-3">
                                    <dt className="w-28 text-[11px] font-semibold text-gray-500 pt-0.5 shrink-0">{label}</dt>
                                    <dd className="text-xs text-gray-900">{value || <span className="text-gray-400">—</span>}</dd>
                                </div>
                            ))}
                            {/* Email — displayed but not editable */}
                            <div className="flex items-start gap-3 pt-1">
                                <dt className="w-28 text-[11px] font-semibold text-gray-500 pt-2 shrink-0">Email</dt>
                                <dd className="flex-1">
                                    <input
                                        type="email"
                                        value={userData.email || user?.email || ''}
                                        disabled
                                        readOnly
                                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed select-none"
                                    />
                                </dd>
                            </div>
                        </dl>
                    )}
                </div>

                {/* Update Phone */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-sm font-bold text-gray-900 mb-1">Update Phone Number</h2>
                    <p className="text-xs text-gray-500 mb-4">Change the phone number associated with your account.</p>
                    <form onSubmit={handleUpdatePhone} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number</label>
                            <input
                                type="tel"
                                value={newPhone}
                                onChange={e => setNewPhone(e.target.value)}
                                placeholder="+1 234 567 8900"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>
                        <button type="submit" disabled={phoneLoading}
                            className="px-5 py-2 text-xs font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white rounded-lg shadow-md shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors">
                            {phoneLoading ? <><Spinner />Updating...</> : 'Update Phone'}
                        </button>
                    </form>
                </div>

                {/* Change Password */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h2 className="text-sm font-bold text-gray-900 mb-1">Change Password</h2>
                    <p className="text-xs text-gray-500 mb-4">Must be at least 8 characters with uppercase, lowercase, digit &amp; special character.</p>
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                        {[
                            { key: 'newPassword', label: 'New Password' },
                            { key: 'confirmPassword', label: 'Confirm New Password' },
                        ].map(({ key, label }) => (
                            <div key={key}>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
                                <div className="relative">
                                    <input
                                        type={showPw[key] ? 'text' : 'password'}
                                        value={pwForm[key]}
                                        onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                                        placeholder="••••••••"
                                        className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                    <button type="button" onClick={() => setShowPw(s => ({ ...s, [key]: !s[key] }))}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                        {showPw[key]
                                            ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                            : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                        }
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button type="submit" disabled={pwLoading}
                            className="px-5 py-2 text-xs font-semibold bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white rounded-lg shadow-md shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors">
                            {pwLoading ? <><Spinner />Updating...</> : 'Change Password'}
                        </button>
                    </form>
                </div>

            </div>
        </div>
    );
};

export default ProfilePage;
